import React, { useState, useEffect } from 'react';
import { inventoryAPI, businessesAPI, creditorsAPI } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';
import { formatApiError } from '../utils/errorMessage';
import { todayLocal } from '../utils/localDate';

// Presets covering both physical goods and common service billing units
// (hour/mile/day/session) - "Custom" reveals a free-text field since the
// underlying column is just a string, so any unit works (e.g. "load", "visit").
const UNIT_PRESETS = ['each', 'hour', 'mile', 'day', 'week', 'session', 'sq ft', 'box', 'case', 'lb', 'kg', 'oz'];
const UNIT_LABELS = {
  each: 'Each', hour: 'Hour', mile: 'Mile', day: 'Day', week: 'Week',
  session: 'Session', 'sq ft': 'Sq Ft', box: 'Box', case: 'Case',
  lb: 'Pound (lb)', kg: 'Kilogram (kg)', oz: 'Ounce (oz)'
};

/**
 * Products & Services Page
 * Single Responsibility: Manage the catalog of products (stock-tracked) and
 * services (billed by hour/mile/session/etc., no stock concept)
 */
const Inventory = ({ selectedBusiness }) => {
  const [items, setItems] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    business_id: selectedBusiness || '',
    sku: '',
    name: '',
    description: '',
    category: '',
    item_type: 'product',
    unit_cost: '',
    selling_price: '',
    quantity_on_hand: '0',
    reorder_point: '0',
    reorder_quantity: '0',
    unit_of_measure: 'each',
    location: '',
    supplier_id: ''
  });
  const [transactionData, setTransactionData] = useState({
    transaction_type: 'purchase',
    quantity: '',
    unit_cost: '',
    transaction_date: todayLocal(),
    reference_number: '',
    notes: ''
  });
  const [filters, setFilters] = useState({
    business_id: selectedBusiness || '',
    category: '',
    low_stock_only: false
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  useEffect(() => {
    setFilters(prev => ({ ...prev, business_id: selectedBusiness || '' }));
    setFormData(prev => ({ ...prev, business_id: selectedBusiness || '' }));
  }, [selectedBusiness]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.business_id) params.business_id = filters.business_id;
      if (filters.category) params.category = filters.category;
      if (filters.low_stock_only) params.low_stock_only = 'true';

      const [itemsRes, businessRes, summaryRes, vendorsRes] = await Promise.all([
        inventoryAPI.getAll(params),
        businessesAPI.getAll(),
        inventoryAPI.getSummary(params),
        creditorsAPI.getAll().catch(() => ({ data: [] }))
      ]);

      setItems(itemsRes.data);
      setBusinesses(businessRes.data);
      setSummary(summaryRes.data);
      setVendors(vendorsRes.data);
      setError('');
    } catch (err) {
      setError(formatApiError(err, 'Failed to load inventory'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedItem) {
        await inventoryAPI.update(selectedItem.id, formData);
      } else {
        await inventoryAPI.create(formData);
      }
      resetForm();
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(formatApiError(err, 'Failed to save inventory item'));
    }
  };

  const handleTransaction = async (e) => {
    e.preventDefault();
    try {
      await inventoryAPI.recordTransaction(selectedItem.id, transactionData);
      setShowTransactionModal(false);
      setTransactionData({
        transaction_type: 'purchase',
        quantity: '',
        unit_cost: '',
        transaction_date: todayLocal(),
        reference_number: '',
        notes: ''
      });
      loadData();
    } catch (err) {
      setError(formatApiError(err, 'Failed to record transaction'));
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setFormData({
      business_id: item.business_id || '',
      sku: item.sku || '',
      name: item.name || '',
      description: item.description || '',
      category: item.category || '',
      item_type: item.item_type || 'product',
      unit_cost: item.unit_cost || '',
      selling_price: item.selling_price || '',
      quantity_on_hand: item.quantity_on_hand || '0',
      reorder_point: item.reorder_point || '0',
      reorder_quantity: item.reorder_quantity || '0',
      unit_of_measure: item.unit_of_measure || 'each',
      location: item.location || '',
      supplier_id: item.supplier_id || ''
    });
    setShowModal(true);
  };

  const openTransactionModal = (item) => {
    setSelectedItem(item);
    setTransactionData({
      ...transactionData,
      unit_cost: item.unit_cost || ''
    });
    setShowTransactionModal(true);
  };

  const resetForm = () => {
    setSelectedItem(null);
    setFormData({
      business_id: selectedBusiness || '',
      sku: '',
      name: '',
      description: '',
      category: '',
      item_type: 'product',
      unit_cost: '',
      selling_price: '',
      quantity_on_hand: '0',
      reorder_point: '0',
      reorder_quantity: '0',
      unit_of_measure: 'each',
      location: '',
      supplier_id: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading && !items.length) {
    return <div className="flex justify-center items-center h-64"><div className="text-gray-500">Loading...</div></div>;
  }

  return (
    <ErrorBoundary section="Inventory">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Products &amp; Services</h1>
            <p className="text-gray-600 mt-1">Manage your catalog - physical products with stock tracking, or services billed by hour, mile, session, and more</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + New Item
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" role="alert">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Items</div>
              <div className="text-2xl font-bold text-gray-800">{summary.total_items}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Value</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(summary.total_value)}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Quantity</div>
              <div className="text-2xl font-bold text-gray-800">{summary.total_quantity}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Low Stock</div>
              <div className="text-2xl font-bold text-red-600">{summary.low_stock_count}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-center">
          <select
            value={filters.business_id}
            onChange={(e) => setFilters({ ...filters, business_id: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">All Businesses</option>
            {businesses.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Category filter"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="border rounded px-3 py-2"
          />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={filters.low_stock_only}
              onChange={(e) => setFilters({ ...filters, low_stock_only: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Low Stock Only</span>
          </label>
        </div>

        {/* Inventory Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty On Hand</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Selling Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map(item => {
                  const isService = item.item_type === 'service';
                  const totalValue = parseFloat(item.quantity_on_hand || 0) * parseFloat(item.unit_cost || 0);
                  const isLowStock = !isService && parseFloat(item.quantity_available || 0) <= parseFloat(item.reorder_point || 0);
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.sku || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.name}
                        {isService && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            per {item.unit_of_measure}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.category || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {isService ? '—' : `${parseFloat(item.quantity_on_hand || 0).toFixed(2)} ${item.unit_of_measure}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.unit_cost)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.selling_price)}{isService ? ` / ${item.unit_of_measure}` : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {isService ? '—' : formatCurrency(totalValue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isService ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">Service</span>
                        ) : isLowStock ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Low Stock</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">In Stock</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        {!isService && (
                          <button
                            onClick={() => openTransactionModal(item)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Adjust
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(item)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {items.length === 0 && (
            <div className="text-center py-8 text-gray-500">No inventory items found</div>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">{selectedItem ? 'Edit Item' : 'New Item'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business</label>
                    <select
                      value={formData.business_id}
                      onChange={(e) => setFormData({ ...formData, business_id: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="">Select business</option>
                      {businesses.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                    <input
                      type="text"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="item_type"
                          checked={formData.item_type === 'product'}
                          onChange={() => setFormData({ ...formData, item_type: 'product' })}
                        />
                        <span>Product (physical, tracks stock)</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="item_type"
                          checked={formData.item_type === 'service'}
                          onChange={() => setFormData({ ...formData, item_type: 'service' })}
                        />
                        <span>Service (billed by hour, mile, session, etc. - no stock)</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.item_type === 'service' ? 'Billed Per' : 'Unit of Measure'}
                    </label>
                    <select
                      value={UNIT_PRESETS.includes(formData.unit_of_measure) ? formData.unit_of_measure : '__custom__'}
                      onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value === '__custom__' ? '' : e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      {UNIT_PRESETS.map((unit) => (
                        <option key={unit} value={unit}>{UNIT_LABELS[unit]}</option>
                      ))}
                      <option value="__custom__">Custom...</option>
                    </select>
                    {!UNIT_PRESETS.includes(formData.unit_of_measure) && (
                      <input
                        type="text"
                        placeholder="e.g. 'load', 'visit', 'sq ft'"
                        value={formData.unit_of_measure}
                        onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2 mt-2"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.unit_cost}
                      onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selling Price{formData.item_type === 'service' && formData.unit_of_measure ? ` (per ${formData.unit_of_measure})` : ''}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.selling_price}
                      onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  {formData.item_type !== 'service' && (
                    <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity On Hand</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.quantity_on_hand}
                      onChange={(e) => setFormData({ ...formData, quantity_on_hand: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Point</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.reorder_point}
                      onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Quantity</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.reorder_quantity}
                      onChange={(e) => setFormData({ ...formData, reorder_quantity: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      placeholder="Warehouse, shelf, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                    <select
                      value={formData.supplier_id}
                      onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="">Select supplier</option>
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>{v.creditor_name}</option>
                      ))}
                    </select>
                  </div>
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {selectedItem ? 'Update' : 'Create'} Item
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Transaction Modal */}
        {showTransactionModal && selectedItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Stock Transaction</h2>
              <div className="text-sm text-gray-600 mb-4">Item: {selectedItem.name}</div>
              <form onSubmit={handleTransaction} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type *</label>
                  <select
                    value={transactionData.transaction_type}
                    onChange={(e) => setTransactionData({ ...transactionData, transaction_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="purchase">Purchase</option>
                    <option value="sale">Sale</option>
                    <option value="adjustment">Adjustment</option>
                    <option value="return">Return</option>
                    <option value="transfer">Transfer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                  <input
                    type="number"
                    step="0.001"
                    required
                    value={transactionData.quantity}
                    onChange={(e) => setTransactionData({ ...transactionData, quantity: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost</label>
                  <input
                    type="number"
                    step="0.01"
                    value={transactionData.unit_cost}
                    onChange={(e) => setTransactionData({ ...transactionData, unit_cost: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Date *</label>
                  <input
                    type="date"
                    required
                    value={transactionData.transaction_date}
                    onChange={(e) => setTransactionData({ ...transactionData, transaction_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                  <input
                    type="text"
                    value={transactionData.reference_number}
                    onChange={(e) => setTransactionData({ ...transactionData, reference_number: e.target.value })}
                    placeholder="PO #, Invoice #, etc."
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={transactionData.notes}
                    onChange={(e) => setTransactionData({ ...transactionData, notes: e.target.value })}
                    rows={2}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowTransactionModal(false)}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Record Transaction
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default Inventory;

